/**
 * Shared design primitives for admin-facing screens (Platform Admin,
 * School Admin, B2B plan selection, admin modals). Captures the same
 * visual language as the redesigned plan-selection / onboarding flows:
 *
 *   - Soft pastel icon blocks instead of solid primary color squares
 *   - Rounded-2xl cards with light borders, no heavy shadows
 *   - Pill-style tabs with subtle active background
 *   - Calm, structured headers with title + optional subtitle + actions
 *
 * These primitives are deliberately small and stateless so any admin
 * screen can compose them without going through a heavy framework.
 */
import React from 'react';
import { ActivityIndicator, ScrollView, TouchableOpacity, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import { goBackOr } from '@/navigation/inAppHistory';
import { ROUTES } from '@/navigation/routes';

import { Card, CardContent } from '@/components/ui/card';
import { Text } from '@/components/ui/text';
import { Button } from '@/components/ui/button';
import { brand } from '../../design-system/theme/tokens';

// Shared palette — kept in sync with PlanSelectionScreenEnhanced.
export const ADMIN_COLORS = {
  primary: brand.primary,
  primarySoft: '#DBE7FF',
  primaryFaint: '#EEF2FF',
  primaryFaintBorder: '#DDE3FF',
  secondary: '#F59E0B',
  success: '#10B981',
  successSoft: '#D1FAE5',
  successFaint: '#ECFDF5',
  successBorder: '#A7F3D0',
  successText: '#047857',
  warning: '#F59E0B',
  warningSoft: '#FEF3C7',
  warningBorder: '#FDE68A',
  warningText: '#92400E',
  error: '#DC2626',
  errorSoft: '#FEE2E2',
  errorBorder: '#FECACA',
  errorText: '#B91C1C',
  background: '#FFFFFF',
  surface: '#F9FAFB',
  border: '#E5E7EB',
  borderSoft: '#F1F5F9',
  text: {
    primary: '#111827',
    secondary: '#4B5563',
    muted: '#9CA3AF',
  },
} as const;

// ─── PageHeader ────────────────────────────────────────────────────────────

export interface AdminPageHeaderProps {
  title: string;
  subtitle?: string;
  /** When provided, a back chevron is rendered. Pass false to suppress. */
  onBack?: (() => void) | false;
  /** Right-aligned action node (button, link, etc). */
  rightSlot?: React.ReactNode;
  /** Material icon name shown next to the title. */
  icon?: keyof typeof MaterialIcons.glyphMap;
}

export function AdminPageHeader({
  title,
  subtitle,
  onBack,
  rightSlot,
  icon,
}: AdminPageHeaderProps) {
  const router = useRouter();
  const showBack = onBack !== false;
  const handleBack = () => {
    if (typeof onBack === 'function') return onBack();
    goBackOr(router, ROUTES.TABS_PLATFORM_ADMIN);
  };

  return (
    <View
      style={{
        backgroundColor: ADMIN_COLORS.background,
        borderBottomWidth: 1,
        borderBottomColor: ADMIN_COLORS.border,
        paddingHorizontal: 24,
        paddingVertical: 16,
      }}
    >
      <View className="flex-row items-center" style={{ gap: 12 }}>
        {showBack ? (
          <TouchableOpacity
            onPress={handleBack}
            accessibilityRole="button"
            hitSlop={8}
            style={{
              width: 36,
              height: 36,
              borderRadius: 18,
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: ADMIN_COLORS.surface,
            }}
          >
            <MaterialIcons name="arrow-back" size={20} color={ADMIN_COLORS.text.primary} />
          </TouchableOpacity>
        ) : null}

        {icon ? (
          <View
            style={{
              width: 40,
              height: 40,
              borderRadius: 20,
              backgroundColor: ADMIN_COLORS.primarySoft,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <MaterialIcons name={icon} size={20} color={ADMIN_COLORS.primary} />
          </View>
        ) : null}

        <View style={{ flex: 1 }}>
          <Text className="text-xl font-bold" style={{ color: ADMIN_COLORS.text.primary }}>
            {title}
          </Text>
          {subtitle ? (
            <Text className="text-sm" style={{ color: ADMIN_COLORS.text.secondary }}>
              {subtitle}
            </Text>
          ) : null}
        </View>

        {rightSlot}
      </View>
    </View>
  );
}

// ─── AdminTabBar ───────────────────────────────────────────────────────────

export interface AdminTab<TKey extends string = string> {
  key: TKey;
  label: string;
  icon: keyof typeof MaterialIcons.glyphMap;
  /** Optional badge count (e.g., pending feedback). */
  badge?: number | null;
}

interface AdminTabBarProps<TKey extends string = string> {
  tabs: ReadonlyArray<AdminTab<TKey>>;
  activeKey: TKey;
  onChange: (key: TKey) => void;
}

export function AdminTabBar<TKey extends string = string>({
  tabs,
  activeKey,
  onChange,
}: AdminTabBarProps<TKey>) {
  return (
    <View
      style={{
        backgroundColor: ADMIN_COLORS.surface,
        borderRadius: 14,
        padding: 6,
        borderWidth: 1,
        borderColor: ADMIN_COLORS.border,
      }}
    >
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{ gap: 4, alignItems: 'center' }}
      >
        {tabs.map((tab) => {
          const active = tab.key === activeKey;
          return (
            <TouchableOpacity
              key={tab.key}
              onPress={() => onChange(tab.key)}
              accessibilityRole="tab"
              accessibilityState={{ selected: active }}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: 8,
                paddingHorizontal: 14,
                paddingVertical: 9,
                borderRadius: 10,
                backgroundColor: active ? ADMIN_COLORS.background : 'transparent',
                borderWidth: active ? 1 : 0,
                borderColor: active ? ADMIN_COLORS.border : 'transparent',
                shadowColor: active ? '#000' : 'transparent',
                shadowOpacity: active ? 0.04 : 0,
                shadowRadius: active ? 6 : 0,
                shadowOffset: { width: 0, height: 1 },
              }}
            >
              <MaterialIcons
                name={tab.icon}
                size={16}
                color={active ? ADMIN_COLORS.primary : ADMIN_COLORS.text.secondary}
              />
              <Text
                style={{
                  fontSize: 13,
                  fontWeight: '600',
                  color: active ? ADMIN_COLORS.primary : ADMIN_COLORS.text.secondary,
                }}
              >
                {tab.label}
              </Text>
              {typeof tab.badge === 'number' && tab.badge > 0 ? (
                <View
                  style={{
                    backgroundColor: active ? ADMIN_COLORS.primary : ADMIN_COLORS.text.muted,
                    minWidth: 18,
                    paddingHorizontal: 6,
                    height: 18,
                    borderRadius: 9,
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Text style={{ color: '#FFFFFF', fontSize: 10, fontWeight: '700' }}>
                    {tab.badge}
                  </Text>
                </View>
              ) : null}
            </TouchableOpacity>
          );
        })}
      </ScrollView>
    </View>
  );
}

// ─── SectionCard ───────────────────────────────────────────────────────────

export interface AdminSectionCardProps {
  /** Optional icon — rendered in a soft pastel block. */
  icon?: keyof typeof MaterialIcons.glyphMap;
  /** Override icon block colors (defaults to primary tint). */
  iconBg?: string;
  iconColor?: string;
  title?: string;
  subtitle?: string;
  /** Right-aligned slot inside the header (badge, action button, etc). */
  headerRight?: React.ReactNode;
  /** Body content. */
  children?: React.ReactNode;
  /** Tighten or loosen body spacing. */
  bodyClassName?: string;
}

export function AdminSectionCard({
  icon,
  iconBg,
  iconColor,
  title,
  subtitle,
  headerRight,
  children,
  bodyClassName,
}: AdminSectionCardProps) {
  const showHeader = Boolean(icon || title || subtitle || headerRight);
  return (
    <Card
      style={{
        borderWidth: 1,
        borderColor: ADMIN_COLORS.border,
        borderRadius: 20,
        backgroundColor: ADMIN_COLORS.background,
      }}
    >
      <CardContent className={bodyClassName ?? 'p-5 md:p-6'} style={{ gap: 16 }}>
        {showHeader ? (
          <View className="flex-row items-start" style={{ gap: 12 }}>
            {icon ? (
              <View
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 12,
                  backgroundColor: iconBg ?? ADMIN_COLORS.primarySoft,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <MaterialIcons
                  name={icon}
                  size={20}
                  color={iconColor ?? ADMIN_COLORS.primary}
                />
              </View>
            ) : null}
            <View style={{ flex: 1 }}>
              {title ? (
                <Text
                  className="text-base font-bold"
                  style={{ color: ADMIN_COLORS.text.primary }}
                >
                  {title}
                </Text>
              ) : null}
              {subtitle ? (
                <Text
                  className="text-sm"
                  style={{ color: ADMIN_COLORS.text.secondary, marginTop: 2 }}
                >
                  {subtitle}
                </Text>
              ) : null}
            </View>
            {headerRight}
          </View>
        ) : null}
        {children}
      </CardContent>
    </Card>
  );
}

// ─── StatCard ──────────────────────────────────────────────────────────────

export interface AdminStatCardProps {
  icon: keyof typeof MaterialIcons.glyphMap;
  label: string;
  value: string | number;
  /** Optional secondary line under the value (e.g., "+12 this month"). */
  hint?: string;
  /** Tint color family (defaults to primary). */
  tone?: 'primary' | 'success' | 'warning' | 'error';
  onPress?: () => void;
}

const TONE_PALETTE: Record<NonNullable<AdminStatCardProps['tone']>, { bg: string; fg: string }> = {
  primary: { bg: ADMIN_COLORS.primarySoft, fg: ADMIN_COLORS.primary },
  success: { bg: ADMIN_COLORS.successSoft, fg: ADMIN_COLORS.successText },
  warning: { bg: ADMIN_COLORS.warningSoft, fg: ADMIN_COLORS.warningText },
  error: { bg: ADMIN_COLORS.errorSoft, fg: ADMIN_COLORS.errorText },
};

export function AdminStatCard({
  icon,
  label,
  value,
  hint,
  tone = 'primary',
  onPress,
}: AdminStatCardProps) {
  const palette = TONE_PALETTE[tone];

  const inner = (
    <View
      style={{
        flex: 1,
        minWidth: 160,
        padding: 18,
        backgroundColor: ADMIN_COLORS.background,
        borderRadius: 16,
        borderWidth: 1,
        borderColor: ADMIN_COLORS.border,
        gap: 12,
      }}
    >
      <View className="flex-row items-center" style={{ gap: 10 }}>
        <View
          style={{
            width: 36,
            height: 36,
            borderRadius: 10,
            backgroundColor: palette.bg,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <MaterialIcons name={icon} size={18} color={palette.fg} />
        </View>
        <Text
          className="text-xs font-semibold uppercase"
          style={{ color: ADMIN_COLORS.text.muted, letterSpacing: 0.5, flex: 1 }}
        >
          {label}
        </Text>
      </View>
      <View>
        <Text
          className="text-3xl font-extrabold"
          style={{ color: ADMIN_COLORS.text.primary }}
        >
          {value}
        </Text>
        {hint ? (
          <Text
            className="text-xs mt-1"
            style={{ color: ADMIN_COLORS.text.secondary }}
          >
            {hint}
          </Text>
        ) : null}
      </View>
    </View>
  );

  if (onPress) {
    return (
      <TouchableOpacity onPress={onPress} activeOpacity={0.85}>
        {inner}
      </TouchableOpacity>
    );
  }
  return inner;
}

// ─── EmptyState ────────────────────────────────────────────────────────────

export interface AdminEmptyStateProps {
  icon: keyof typeof MaterialIcons.glyphMap;
  title: string;
  body?: string;
  cta?: { label: string; onPress: () => void; loading?: boolean };
}

export function AdminEmptyState({ icon, title, body, cta }: AdminEmptyStateProps) {
  return (
    <View
      style={{
        alignItems: 'center',
        paddingHorizontal: 24,
        paddingVertical: 36,
      }}
    >
      <View
        style={{
          width: 64,
          height: 64,
          borderRadius: 32,
          backgroundColor: ADMIN_COLORS.primarySoft,
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 14,
        }}
      >
        <MaterialIcons name={icon} size={28} color={ADMIN_COLORS.primary} />
      </View>
      <Text
        className="text-lg font-bold text-center"
        style={{ color: ADMIN_COLORS.text.primary }}
      >
        {title}
      </Text>
      {body ? (
        <Text
          className="text-sm text-center"
          style={{
            color: ADMIN_COLORS.text.secondary,
            marginTop: 6,
            maxWidth: 420,
          }}
        >
          {body}
        </Text>
      ) : null}
      {cta ? (
        <View style={{ marginTop: 16 }}>
          <Button onPress={cta.onPress} disabled={cta.loading} variant="default">
            {cta.loading ? (
              <View className="flex-row items-center" style={{ gap: 8 }}>
                <ActivityIndicator size="small" color="#FFFFFF" />
                <Text className="text-white font-semibold">{cta.label}</Text>
              </View>
            ) : (
              <Text className="text-white font-semibold">{cta.label}</Text>
            )}
          </Button>
        </View>
      ) : null}
    </View>
  );
}

// ─── CalloutBanner ─────────────────────────────────────────────────────────

export interface AdminCalloutBannerProps {
  tone: 'info' | 'success' | 'warning' | 'error';
  icon?: keyof typeof MaterialIcons.glyphMap;
  title?: string;
  body: string;
  /** Optional action node aligned to the right. */
  action?: React.ReactNode;
}

const CALLOUT_PALETTE: Record<
  AdminCalloutBannerProps['tone'],
  { bg: string; border: string; text: string; iconBg: string }
> = {
  info: {
    bg: ADMIN_COLORS.primaryFaint,
    border: ADMIN_COLORS.primaryFaintBorder,
    text: ADMIN_COLORS.primary,
    iconBg: ADMIN_COLORS.primarySoft,
  },
  success: {
    bg: ADMIN_COLORS.successFaint,
    border: ADMIN_COLORS.successBorder,
    text: ADMIN_COLORS.successText,
    iconBg: ADMIN_COLORS.successSoft,
  },
  warning: {
    bg: ADMIN_COLORS.warningSoft,
    border: ADMIN_COLORS.warningBorder,
    text: ADMIN_COLORS.warningText,
    iconBg: '#FDE68A',
  },
  error: {
    bg: ADMIN_COLORS.errorSoft,
    border: ADMIN_COLORS.errorBorder,
    text: ADMIN_COLORS.errorText,
    iconBg: '#FECACA',
  },
};

const DEFAULT_TONE_ICON: Record<AdminCalloutBannerProps['tone'], keyof typeof MaterialIcons.glyphMap> = {
  info: 'info-outline',
  success: 'check-circle',
  warning: 'warning-amber',
  error: 'error-outline',
};

export function AdminCalloutBanner({
  tone,
  icon,
  title,
  body,
  action,
}: AdminCalloutBannerProps) {
  const palette = CALLOUT_PALETTE[tone];
  const iconName = icon ?? DEFAULT_TONE_ICON[tone];

  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 12,
        padding: 14,
        borderRadius: 14,
        backgroundColor: palette.bg,
        borderWidth: 1,
        borderColor: palette.border,
      }}
    >
      <View
        style={{
          width: 32,
          height: 32,
          borderRadius: 16,
          backgroundColor: palette.iconBg,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <MaterialIcons name={iconName} size={16} color={palette.text} />
      </View>
      <View style={{ flex: 1 }}>
        {title ? (
          <Text
            className="text-sm font-bold"
            style={{ color: palette.text, marginBottom: 2 }}
          >
            {title}
          </Text>
        ) : null}
        <Text className="text-sm" style={{ color: palette.text }}>
          {body}
        </Text>
      </View>
      {action}
    </View>
  );
}

// ─── KeyValueList ──────────────────────────────────────────────────────────
// Tight little label/value rows for plan summaries, billing details, etc.

export interface AdminKeyValueRow {
  label: string;
  value: React.ReactNode;
}

export function AdminKeyValueList({ rows }: { rows: AdminKeyValueRow[] }) {
  return (
    <View>
      {rows.map((row, i) => (
        <View
          key={`${row.label}-${i}`}
          style={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingVertical: 10,
            borderTopWidth: i === 0 ? 0 : 1,
            borderTopColor: ADMIN_COLORS.borderSoft,
            gap: 16,
          }}
        >
          <Text
            className="text-sm"
            style={{ color: ADMIN_COLORS.text.secondary, flexShrink: 1 }}
          >
            {row.label}
          </Text>
          <View style={{ flexShrink: 0, alignItems: 'flex-end' }}>
            {typeof row.value === 'string' || typeof row.value === 'number' ? (
              <Text
                className="text-sm font-semibold"
                style={{ color: ADMIN_COLORS.text.primary }}
              >
                {row.value}
              </Text>
            ) : (
              row.value
            )}
          </View>
        </View>
      ))}
    </View>
  );
}

// ─── StatusPill ────────────────────────────────────────────────────────────

export interface AdminStatusPillProps {
  label: string;
  tone?: 'neutral' | 'primary' | 'success' | 'warning' | 'error';
  icon?: keyof typeof MaterialIcons.glyphMap;
}

const PILL_PALETTE: Record<
  NonNullable<AdminStatusPillProps['tone']>,
  { bg: string; fg: string; border: string }
> = {
  neutral: {
    bg: ADMIN_COLORS.surface,
    fg: ADMIN_COLORS.text.secondary,
    border: ADMIN_COLORS.border,
  },
  primary: {
    bg: ADMIN_COLORS.primaryFaint,
    fg: ADMIN_COLORS.primary,
    border: ADMIN_COLORS.primaryFaintBorder,
  },
  success: {
    bg: ADMIN_COLORS.successFaint,
    fg: ADMIN_COLORS.successText,
    border: ADMIN_COLORS.successBorder,
  },
  warning: {
    bg: ADMIN_COLORS.warningSoft,
    fg: ADMIN_COLORS.warningText,
    border: ADMIN_COLORS.warningBorder,
  },
  error: {
    bg: ADMIN_COLORS.errorSoft,
    fg: ADMIN_COLORS.errorText,
    border: ADMIN_COLORS.errorBorder,
  },
};

export function AdminStatusPill({
  label,
  tone = 'neutral',
  icon,
}: AdminStatusPillProps) {
  const palette = PILL_PALETTE[tone];
  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 999,
        backgroundColor: palette.bg,
        borderWidth: 1,
        borderColor: palette.border,
        alignSelf: 'flex-start',
      }}
    >
      {icon ? <MaterialIcons name={icon} size={12} color={palette.fg} /> : null}
      <Text
        style={{
          fontSize: 11,
          fontWeight: '700',
          color: palette.fg,
          letterSpacing: 0.3,
        }}
      >
        {label}
      </Text>
    </View>
  );
}
